import React from 'react';
import type { LoyaltyProgramType } from '../../../shared/types';
import { StarIcon, StampIcon, WalletIcon, TargetIcon } from '../icons/UiIcons';

interface TypeSelectorProps {
    /** Type actuellement sélectionné. */
    value: LoyaltyProgramType;
    /** Appelé quand le restaurateur sélectionne Points ou Tampons. */
    onChange: (type: LoyaltyProgramType) => void;
    /** Désactive le sélecteur (ex: programme legacy en lecture seule). */
    disabled?: boolean;
}

interface TypeCard {
    id: LoyaltyProgramType;
    label: string;
    description: string;
    icon: React.ReactNode;
    available: boolean;
}

// Ordre fixe imposé par le brief : Points / Tampons / Cagnotte / Missions.
const CARDS: TypeCard[] = [
    { id: 'points',   label: 'Points',   description: 'Le client cumule des points par euro dépensé', icon: <StarIcon className="w-6 h-6" />,   available: true },
    { id: 'stamps',   label: 'Tampons',  description: 'Une visite = un tampon, un objectif à atteindre', icon: <StampIcon className="w-6 h-6" />,  available: true },
    { id: 'spending', label: 'Cagnotte', description: 'Le client se constitue une cagnotte en €',     icon: <WalletIcon className="w-6 h-6" />, available: false },
    { id: 'missions', label: 'Missions', description: 'Des défis ludiques à compléter pour gagner',   icon: <TargetIcon className="w-6 h-6" />, available: false },
];

const TypeSelector: React.FC<TypeSelectorProps> = ({ value, onChange, disabled }) => {
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {CARDS.map(card => {
                const isSelected = value === card.id;
                const isClickable = card.available && !disabled;
                return (
                    <button
                        key={card.id}
                        type="button"
                        onClick={() => isClickable && onChange(card.id)}
                        disabled={!isClickable}
                        aria-pressed={isSelected}
                        className={`relative p-4 rounded-2xl border-2 text-left transition-all ${
                            isSelected
                                ? 'border-brand-primary bg-brand-primary/5 dark:bg-brand-primary/15 shadow-sm'
                                : isClickable
                                    ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-brand-primary/40 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                                    : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 cursor-not-allowed'
                        }`}
                    >
                        {!card.available && (
                            <span className="absolute top-2 right-2 text-xs font-bold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">
                                Prochainement
                            </span>
                        )}
                        <div className={`w-10 h-10 rounded-xl mb-3 flex items-center justify-center ${
                            isSelected
                                ? 'bg-brand-primary text-white'
                                : !card.available
                                    ? 'bg-gray-200 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                                    : 'bg-brand-primary/10 dark:bg-brand-primary/20 text-brand-primary'
                        }`}>
                            {card.icon}
                        </div>
                        <div className={`font-bold text-sm ${!card.available ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
                            {card.label}
                        </div>
                        <div className={`text-xs mt-1 ${!card.available ? 'text-gray-400 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}>
                            {card.description}
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

export default TypeSelector;
